import { useEffect, useState } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Logo } from "./Logo";

const links = [
  { key: "home", href: "#home", testid: "nav-link-home" },
  { key: "about", href: "#about", testid: "nav-link-about" },
  { key: "services", href: "#services", testid: "nav-link-services" },
  { key: "booking", href: "#booking", testid: "nav-link-booking" },
  { key: "faq", href: "#faq", testid: "nav-link-faq" },
  { key: "contact", href: "#contact", testid: "nav-link-contact" },
];

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className={`flex items-center rounded-full border border-teal-400/30 bg-white/5 p-0.5 ${className}`}
      data-testid="language-switcher"
    >
      {["cs", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-teal-500 text-[#052E2B]" : "text-slate-400 hover:text-teal-300"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    window.history.replaceState(null, "", href);
    if (window.__lenis) {
      window.__lenis.scrollTo(el, {
        offset: -72,
        duration: 1.4,
        easing: (t) => 1 - Math.pow(1 - t, 4),
      });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-teal-500/15 bg-[#050B14]/90 backdrop-blur" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo variant="light" />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => goTo(e, l.href)}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-[#2DD4BF]"
            >
              {t.nav[l.key][lang]}
            </a>
          ))}
          <a
            href="/matrix"
            data-testid="nav-link-matrix"
            className="flex items-center gap-1.5 text-sm font-semibold text-teal-300 transition-colors duration-200 hover:text-[#2DD4BF]"
          >
            <Sparkles size={14} />
            {t.nav.matrix[lang]}
          </a>
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <LangToggle />
          <a
            href="#booking"
            data-testid="nav-book-button"
            onClick={(e) => goTo(e, "#booking")}
            className="rounded-full bg-[#0D9488] px-5 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#14B8A6] hover:shadow-[0_0_20px_rgba(45,212,191,0.35)]"
          >
            {t.nav.bookBtn[lang]}
          </a>
        </div>
        <button
          data-testid="mobile-menu-button"
          className="rounded-md p-2 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} className="text-white" /> : <Menu size={22} className="text-white" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="border-t border-teal-500/15 bg-[#050B14] px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={(e) => goTo(e, l.href)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5"
              >
                {t.nav[l.key][lang]}
              </a>
            ))}
            <a
              href="/matrix"
              data-testid="mobile-nav-link-matrix"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold text-teal-300 hover:bg-white/5"
            >
              <Sparkles size={14} /> {t.nav.matrix[lang]}
            </a>
            <div className="mt-2 flex items-center justify-between px-3">
              <LangToggle />
              <a
                href="#booking"
                data-testid="mobile-nav-book-button"
                onClick={(e) => goTo(e, "#booking")}
                className="rounded-full bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white"
              >
                {t.nav.bookBtn[lang]}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
